import { useState, useEffect } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Layout from '@sdk/login/components/layout/Layout';
import DesktopFullWidthRow from '@sdk/login/components/layout/DesktopFullWidthRow';
import ClickableMediumLogo from '@sdk/login/components/layout/ClickableMediumLogo';
import ScreenContainer from '@sdk/login/components/layout/ScreenContainer';
import H2 from '@sdk/components/ui/typography/H2';
import Text14 from '@sdk/components/ui/typography/Text14';
import Row from '@sdk/components/ui/spacing/Row';
import MobilePhonesSidebar from '@sdk/login/components/sidebars/MobilePhonesSidebar';
import { Container, LayoutContent, Sidebar, Main, Content } from '@sdk/login';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import HorizontalSeparatorWithText from '@sdk/login/components/HorizontalSeparatorWithText';
import RoundedIconButton from '@sdk/login/components/RoundedIconButton';
import Margin from '@sdk/components/ui/spacing/Margin';
import GmailIcon from '@sdk/assets/svg/UI/gmail.svg';
import OutlookIcon from '@sdk/assets/svg/UI/outlook.svg';
import YahooIcon from '@sdk/assets/svg/UI/yahoo.svg';
import useAnalytics from '@sdk/analytics/useAnalytics';
import { AnalyticsEvents } from '@sdk/analytics/types';

import config from '../../src/config';

const CoverContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1040;
`;

const Email = styled.span`
  color: ${({ theme }): string => theme.colors.primary};
`;

const ErrorMsg = styled.div`
  color: ${({ theme }): string => theme.colors.danger};
`;

const SuccessMsg = styled.div`
  color: ${({ theme }): string => theme.colors.primary};
`;

const InboxLinksContainer = styled(Row)`
  justify-content: space-between;
  align-items: center;
`;

const GrowMargin = styled(Margin)`
  flex: 1;
`;

const LinkButton = styled.a`
  display: flex;
  flex: 1;
`;

type Props = {
  userEmail: string;
  resendVerification: () => void;
  logout: () => void;
};

const VerifyEmailCover: React.FC<React.PropsWithChildren<Props>> = ({
  userEmail,
  resendVerification,
  logout,
}) => {
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { track } = useAnalytics();

  useEffect(() => {
    track(AnalyticsEvents.signupOnboardingVerify);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackInboxLinkClicked =
    (value: string, href: string) =>
    async (event: React.MouseEvent): Promise<void> => {
      event.preventDefault();
      await track(AnalyticsEvents.signupInboxLinkClicked, value);
      window.location.href = href;
    };

  const backToLogin = async (event: React.MouseEvent): Promise<void> => {
    event.preventDefault();
    await track(AnalyticsEvents.signupRestart);
    await logout();
    window.location.href = config.SLING_LOGIN_URL + '/register';
  };

  const resendEmail = async (): Promise<void> => {
    try {
      await resendVerification();
      setSuccessMessage(gettext('Verification email resent. Check your inbox.'));
    } catch (e) {
      const message = e.message || gettext('Server error');
      setServerError(message);
      throw e;
    }
  };

  return (
    <CoverContainer>
      <Container>
        <LayoutContent>
          <Sidebar>
            <MobilePhonesSidebar partnerBanner={null} />
          </Sidebar>
          <Main>
            <Content>
              <Layout>
                <DesktopFullWidthRow>
                  <ClickableMediumLogo />
                  <span>
                    Wrong email?{' '}
                    <a href={config.SLING_LOGIN_URL + '/register'} onClick={backToLogin}>
                      {gettext('Start over')}
                    </a>
                  </span>
                </DesktopFullWidthRow>

                <ScreenContainer>
                  <Row top={0}>
                    <H2 themeColor={ThemeColorType.primaryText}>
                      {gettext('Go check your email!')}
                    </H2>
                  </Row>
                  <Row top={20}>
                    <Text14 themeColor={ThemeColorType.gray}>
                      {gettext(
                        'Before you can log into your Sling account, we need you to verify your email address.'
                      )}
                      <br />
                      <TranslateWithComponents
                        replaceComponents={{ email: <Email>{userEmail}</Email> }}>
                        {gettext('We sent a link to [[email]].')}
                      </TranslateWithComponents>
                    </Text14>
                  </Row>
                  <Row top={40}>
                    <ProgressButton
                      buttonType={ButtonType.primary}
                      onClick={resendEmail}
                      height={42}>
                      {gettext('Resend verification email')}
                    </ProgressButton>
                  </Row>
                  <Row top={20} bottom={20}>
                    <HorizontalSeparatorWithText text={gettext('Open')} />
                  </Row>
                  <InboxLinksContainer>
                    <GrowMargin>
                      <LinkButton href={'https://mail.google.com'} target="_blank">
                        <RoundedIconButton
                          onClick={trackInboxLinkClicked('gmail', 'https://mail.google.com')}>
                          <GmailIcon />
                        </RoundedIconButton>
                      </LinkButton>
                    </GrowMargin>
                    <GrowMargin>
                      <LinkButton href={'https://outlook.com/'} target="_blank">
                        <RoundedIconButton
                          onClick={trackInboxLinkClicked('outlook', 'https://outlook.com/')}>
                          <OutlookIcon></OutlookIcon>
                        </RoundedIconButton>
                      </LinkButton>
                    </GrowMargin>
                    <GrowMargin>
                      <LinkButton href={'https://mail.yahoo.com'} target="_blank">
                        <RoundedIconButton
                          onClick={trackInboxLinkClicked('yahoo', 'https://mail.yahoo.com')}>
                          <YahooIcon></YahooIcon>
                        </RoundedIconButton>
                      </LinkButton>
                    </GrowMargin>
                  </InboxLinksContainer>
                  {serverError ? (
                    <Row top={20}>
                      <ErrorMsg>{serverError}</ErrorMsg>
                    </Row>
                  ) : null}
                  {successMessage ? (
                    <Row top={20}>
                      <SuccessMsg>{successMessage}</SuccessMsg>
                    </Row>
                  ) : null}
                </ScreenContainer>
              </Layout>
            </Content>
          </Main>
        </LayoutContent>
      </Container>
    </CoverContainer>
  );
};

export default VerifyEmailCover;
